import { useState, useCallback, useEffect, useMemo } from 'react';
import { getSummaryHotEventListV2 } from '@op/services';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getGameIdIsSame } from '@op/utils/url';

interface UseHotEventSummaryReportOptions {
  autoFetch?: boolean;
}

/**
 * Hot Event Summary 报告 Hook
 * 使用 /summary/hot_event/list_v2 (view_mode=summary) 获取 GPT 摘要报告
 */
export const useHotEventSummaryReport = (
  gameInfo: any,
  filter: any,
  sentimentValue: string,
  channelType: string,
  options?: UseHotEventSummaryReportOptions,
  clusterCategoriesValue?: string,
) => {
  const { autoFetch = true } = options || {};
  const [reportCn, setReportCn] = useState('');
  const [reportEn, setReportEn] = useState('');

  const { loading, wrapperCancelRequest } = useCancelRequestHook();

  const filterParams = useMemo(() => {
    const { isSameId } = getGameIdIsSame(gameInfo);
    if (!isSameId || filter === null) return undefined;
    return {
      ...gameInfo,
      view_mode: 'summary',
      date_type: 'day',
      sentiment_type: sentimentValue || 'total',
      channel_type: channelType,
      ...(filter || {}),
      exclude_report_url_text: true,
      cluster_categories: clusterCategoriesValue && clusterCategoriesValue !== 'all'
        ? [clusterCategoriesValue]
        : [],
    };
  }, [gameInfo, filter, sentimentValue, channelType, clusterCategoriesValue]);

  const fetchReport = useCallback(
    (params: any) => {
      return wrapperCancelRequest(getSummaryHotEventListV2, params).then(
        (data: any) => {
          const responseData = data?.data || data || {};
          setReportCn(responseData.report_cn || '');
          setReportEn(responseData.report_en || '');
          return responseData;
        },
      );
    },
    [], // ⚠️ 依赖数组必须为空
  );

  useEffect(() => {
    if (!filterParams || !autoFetch) return;
    fetchReport(filterParams);
  }, [filterParams, autoFetch, fetchReport]);

  return {
    reportCn,
    reportEn,
    loading,
    fetchReport,
  };
};
